import { useRouter } from "next/router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { altDataUpgradeDialogState, planPickerDrawerOpenState, userState } from "@/lib/store";
import useAppSubscriberUpgrade from "src/hooks/useAppSubscriberUpgrade";
import { UPGRADE_LOCATIONS, trackUpgradeClick } from "@/lib/tracking";
import { isOrtexApp } from "@/lib/utils";

// Alt Data is part of ORTEX Advanced and above. Visitors, Free and Basic
// members land here (from the menu, or from any /alt-data route) instead of on
// a dead page. Same CTA routing as the Index Rebalance / Options unlock dialogs.
const upgradeCtaText: Record<string, string> = {
    "ORTEX Visitor": "Create free account",
    "ORTEX Free": "Upgrade now",
    "ORTEX Basic": "Get Advanced",
};

const AltDataUpgradeDialog = () => {
    const [open, setOpen] = useRecoilState(altDataUpgradeDialogState);
    const user = useRecoilValue(userState);
    const setPlanPickerOpen = useSetRecoilState(planPickerDrawerOpenState);
    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();
    const { push } = useRouter();

    const close = () => setOpen(false);

    const handleUpgrade = () => {
        trackUpgradeClick(UPGRADE_LOCATIONS.ALT_DATA_UPGRADE_DIALOG, { plan: user?.subscription_plan });
        close();
        if (user?.subscription_plan === "ORTEX Visitor") {
            push("/login?register=true");
        } else if (isOrtexApp()) {
            setPlanPickerOpen(true);
        } else {
            handleAppSubscriberUpgrade();
        }
    };

    return (
        <Dialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby="alt-data-upgrade-title">
            <DialogContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 1, display: "grid", placeItems: "center", backgroundColor: "primary.main", color: "primary.contrastText" }}>
                        <InsightsOutlinedIcon />
                    </Box>
                    <Typography id="alt-data-upgrade-title" variant="h2" sx={{ fontWeight: 700 }}>
                        Alt Data is part of ORTEX Advanced
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                    Alternative data (shipping, reactor output, card spend, web traffic and more) mapped to the companies it moves, with the signal changes surfaced on each stock.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Included with ORTEX Advanced, Professional and Institutional subscriptions.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button variant="text" onClick={close}>
                    Maybe later
                </Button>
                <Button variant="contained" onClick={handleUpgrade}>
                    {upgradeCtaText[user?.subscription_plan] || "Upgrade"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AltDataUpgradeDialog;
